/**
 * `runActivationProjectionPull` — session-activation orchestration
 * that drains pending audit-chain events + federated-query aggregations
 * from the SHT-side buffer, encrypts under the patient's session-
 * resident KEK, and writes ciphertext patient-projection records.
 *
 * **Pattern B implementation.** Per Issue #51 closing 2026-05-05:
 * audit-chain events + federated-query aggregations buffer at SHT in
 * cleartext metadata form; the patient session pulls them on
 * activation, renders + encrypts + writes client-side. This module is
 * the client-side ingest entry point — call once after auth + KEK
 * release completes per session activation.
 *
 * **Two ingest sources, one write path.** Audit-chain events carry an
 * `AuditRecord` that the renderer translates into patient-comprehensible
 * narrative; federated-query aggregations carry a cleartext payload
 * (already patient-comprehensible holder-side data) that ships through
 * unchanged. Both end up as `PatientProjectionRecord` ciphertext at
 * SHT under the same content-encryption envelope.
 *
 * **Content shape.** Each record's encrypted content is a JSON
 * serialization of a `PatientProjectionContent` discriminated union
 * (see `./projection-content.ts`-equivalent shape inline below) so the
 * read-side has enough information to re-render under the patient's
 * current locale (Pattern B locale-flexibility: rendering happens at
 * read-time, not write-time, so locale switches don't require
 * re-pulling).
 *
 * **Idempotency.** Record IDs are deterministic functions of
 * `sourceAuditRecordId` (audit-chain) or `sourceOperationId`
 * (aggregation). Re-pulling after a partial-success write produces
 * the same record IDs; the SHT-side store SHOULD be idempotent on
 * `(tenantId, recordId)` (existing `IPatientProjectionStore.put`
 * contract is `outcome: 'ok' | 'not-found' | 'tenant-mismatch' |
 * 'cross-tenant-violation'` — `ok` for first write; subsequent writes
 * to a real backend would benefit from explicit
 * `'already-exists' → ok` idempotency. Production storage backend
 * (DEV3 claim-queue item 6) addresses this).
 *
 * **Error policy.** Per-event failures (render error, encrypt error,
 * service-side write error) DO NOT abort the pull — they're collected
 * into a `PullErrors` array and surfaced to the caller. The active
 * session continues; the patient sees partial-progress rendering.
 * Subsequent activations re-pull and re-attempt the failed records
 * (server-side buffer SHOULD persist failed records until the client
 * acks; v0 mock drains on read, which means failure loses the event
 * — flagged for production-pipeline-PR design).
 *
 * **Out-of-scope concerns flagged for joint production-pipeline PR.**
 * Per Issue #51 closing follow-on three engineering questions:
 *   1. TTL on SHT-side cleartext buffer.
 *   2. Cross-device session consistency (idempotency keyed on
 *      `(patientId, eventId)` proposed; `(patientId, operationId)`
 *      for aggregations).
 *   3. Per-target retention semantics for federated-query rejections.
 * v0 mock-backed flow doesn't address these; production wiring does.
 */
import { writePatientProjectionRecord } from './session-orchestration.js';
const CONTENT_ENCODING = new TextEncoder();
/**
 * Serialize cleartext content to UTF-8 JSON bytes. Round-trips with
 * `decodeContent`. Stable across versions because the discriminator
 * is part of the JSON shape; future envelopes add new `kind` values.
 */
export function encodeContent(content) {
    return CONTENT_ENCODING.encode(JSON.stringify(content));
}
/**
 * Deserialize cleartext content from UTF-8 JSON bytes. Throws if the
 * bytes don't parse or carry an unknown `kind` discriminator.
 */
export function decodeContent(bytes) {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    const parsed = JSON.parse(text);
    if (parsed.kind === 'audit-chain') {
        return parsed;
    }
    if (parsed.kind === 'aggregated-payload') {
        return parsed;
    }
    throw new Error(`unrecognized PatientProjectionContent kind: ${String(parsed.kind)}`);
}
// ---------- Pull entry point ----------
/**
 * Run the full session-activation projection pull. Drains both
 * pending sources (aggregations and audit-chain events), encrypts +
 * writes ciphertext records, and returns a summary.
 *
 * Caller invokes this once per session activation, after
 * `loadUnwrappedKek` completes. Idempotent on per-event recordIds;
 * re-running after a partial-success run will re-attempt failed
 * writes (subject to the server-side buffer's drain-on-read
 * semantics, which v0 mock implements but production storage backend
 * must address).
 */
export async function runActivationProjectionPull(opts) {
    const { session, client, adapter, tenantId, kekVersion } = opts;
    const now = opts.now ?? (() => new Date());
    // Borrow KEK reference for the duration of the pull. The borrow
    // protocol per ActiveSession.requireKek means we MUST NOT retain
    // the reference past this function — and we don't: it lives only
    // on the local stack until pull completes.
    const { kekId, key: unwrappedKek } = session.requireKek();
    const subjectPciHash = session.subject.subjectPciHash;
    const errors = [];
    const writtenRecords = [];
    let aggregationsIngested = 0;
    let auditEventsIngested = 0;
    // ---- 1. Drain pending federated-query aggregations ----
    const pendingAggregations = await client.drainPendingAggregations({
        subjectPciHash,
        ...(tenantId !== undefined ? { tenantId } : {}),
    });
    for (const pending of pendingAggregations) {
        try {
            const content = {
                kind: 'aggregated-payload',
                aggregatedResult: pending.aggregatedResult,
                sourceOperationId: pending.sourceOperationId,
                aggregatedAtSubstrate: pending.aggregatedAtSubstrate,
            };
            const record = await writePatientProjectionRecord({
                input: {
                    tenantId: pending.tenantId,
                    subjectPciHash: pending.subjectPciHash,
                    recordId: pending.recordId,
                    sourceAuditRecordId: pending.sourceOperationId,
                    contentCategory: pending.contentCategory,
                    projectionAnchoredAt: now().toISOString(),
                    sourceAuditAnchoredAt: pending.aggregatedAtSubstrate,
                    contentPlaintext: encodeContent(content),
                    unwrappedKek,
                    kekId,
                    kekVersion,
                },
                adapter,
                client,
            });
            writtenRecords.push(record);
            aggregationsIngested += 1;
        }
        catch (e) {
            errors.push({
                cause: classifyError(e),
                recordId: pending.recordId,
                source: 'aggregation',
                message: e instanceof Error ? e.message : String(e),
            });
        }
    }
    // ---- 2. Drain pending audit-chain events ----
    const pendingAuditEvents = await client.drainPendingAuditChainEvents({
        subjectPciHash,
        ...(tenantId !== undefined ? { tenantId } : {}),
    });
    for (const pending of pendingAuditEvents) {
        try {
            const content = {
                kind: 'audit-chain',
                auditRecord: pending.auditRecord,
            };
            const record = await writePatientProjectionRecord({
                input: {
                    tenantId: pending.tenantId,
                    subjectPciHash: pending.subjectPciHash,
                    recordId: pending.recordId,
                    sourceAuditRecordId: pending.sourceAuditRecordId,
                    // Content category derived from operationType at render
                    // time; storage just needs a category. Use 'operation-record'
                    // as the audit-chain default; categories are refined by the
                    // editorial taxonomy at read-time render.
                    contentCategory: deriveStorageCategory(pending.auditRecord),
                    projectionAnchoredAt: now().toISOString(),
                    sourceAuditAnchoredAt: pending.anchoredAt,
                    contentPlaintext: encodeContent(content),
                    unwrappedKek,
                    kekId,
                    kekVersion,
                },
                adapter,
                client,
            });
            writtenRecords.push(record);
            auditEventsIngested += 1;
        }
        catch (e) {
            errors.push({
                cause: classifyError(e),
                recordId: pending.recordId,
                source: 'audit-chain',
                message: e instanceof Error ? e.message : String(e),
            });
        }
    }
    return {
        aggregationsIngested,
        auditEventsIngested,
        errors,
        writtenRecords,
    };
}
function classifyError(e) {
    if (e instanceof Error) {
        const name = e.name;
        if (name === 'SessionProjectionOrchestrationError') {
            return 'store-write-failed';
        }
        if (name === 'EnvelopeCryptoAdapterError' || name === 'EnvelopeCryptoError') {
            return 'encrypt-failed';
        }
    }
    return 'unknown';
}
/**
 * Storage-side content category derived from `operationType`. The
 * read-side renderer may produce a finer-grained category at render
 * time; the storage-side category exists for category-filter queries
 * (`PatientProjectionRequest.contentCategories`).
 */
function deriveStorageCategory(record) {
    switch (record.operationType) {
        case 'consent-issuance':
        case 'consent-modification':
        case 'consent-revocation':
        case 'delegation-issuance':
            return 'consent-artifact';
        case 'subject-erasure':
            return 'erasure-event';
        default:
            return 'operation-record';
    }
}
//# sourceMappingURL=activation-pull.js.map